import { computed, inject, Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { AppError } from '@/app/core/http/app-error.model';
import { toAppError } from '@/app/core/http/error-catalog';
import { SettlementFilters, SettlementSummary } from '../models/settlement-query.model';
import { SettlementQueryRepository } from '../services/settlement-query.repository';
import { defaultSettlementFilters, sumAmounts } from '../utils/settlement-display.util';

export type SettlementsStatus = 'loading' | 'ready' | 'empty' | 'error';

const ERROR_SUBJECT = 'las liquidaciones cargadas';

@Injectable()
export class SettlementsStore {
  private readonly repository = inject(SettlementQueryRepository);

  private readonly _status = signal<SettlementsStatus>('loading');
  private readonly _records = signal<readonly SettlementSummary[]>([]);
  private readonly _filters = signal<SettlementFilters>(defaultSettlementFilters());
  private readonly _error = signal<AppError | null>(null);
  private inFlight?: Subscription;

  readonly status = this._status.asReadonly();
  readonly records = this._records.asReadonly();
  readonly filters = this._filters.asReadonly();
  readonly error = this._error.asReadonly();

  readonly isLoading = computed(() => this._status() === 'loading');
  readonly totalAmount = computed(() => sumAmounts(this._records().map((r) => r.totalAmount)));

  // El store vive en la ruta: al volver del detalle, el formulario parte con los filtros por defecto.
  reset(): void {
    this.inFlight?.unsubscribe();
    this._filters.set(defaultSettlementFilters());
    this._records.set([]);
    this._error.set(null);
    this._status.set('loading');
  }

  search(filters: SettlementFilters): void {
    this._filters.set(filters);
    this.load();
  }

  load(): void {
    this.inFlight?.unsubscribe();
    this._error.set(null);
    this._status.set('loading');

    this.inFlight = this.repository.search(this._filters()).subscribe({
      next: (records) => {
        this._records.set(records);
        this._status.set(records.length ? 'ready' : 'empty');
      },
      error: (cause: unknown) => {
        this._records.set([]);
        this._error.set(toAppError(cause, ERROR_SUBJECT));
        this._status.set('error');
      },
    });
  }
}
